import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';

export const ImageSlider = ({ images = [], title = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Calculate safe images length (avoid division by zero)
  const imagesLength = images?.length || 1;

  const goToNext = useCallback(() => {
    if (isAnimating || !images?.length) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % imagesLength);
    setTimeout(() => setIsAnimating(false), 500);
  }, [imagesLength, isAnimating, images?.length]);

  const goToPrev = useCallback(() => {
    if (isAnimating || !images?.length) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + imagesLength) % imagesLength);
    setTimeout(() => setIsAnimating(false), 500);
  }, [imagesLength, isAnimating, images?.length]);

  const goToIndex = useCallback((index) => {
    if (isAnimating || index === currentIndex || !images?.length) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, currentIndex, images?.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'Escape') setIsLightboxOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, goToNext, goToPrev]);

  // Auto-play
  useEffect(() => {
    if (isLightboxOpen || !images?.length) return;
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext, isLightboxOpen, images?.length]);

  // If no images, return null (AFTER all hooks)
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <>
      {/* Main Slider */}
      <div className="relative group" data-testid="image-slider">
        {/* Main Image Container */}
        <div 
          className="relative aspect-[16/10] overflow-hidden rounded-sm bg-[#F4F4F5] cursor-pointer"
          onClick={() => setIsLightboxOpen(true)}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className={`
                absolute inset-0 transition-all duration-500 ease-out
                ${index === currentIndex 
                  ? 'opacity-100 scale-100 z-10' 
                  : 'opacity-0 scale-105 z-0'
                }
              `}
            >
              <img
                src={image}
                alt={`${title} - Bild ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}

          {/* Zoom Icon Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
            <ZoomIn 
              size={40} 
              className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
            />
          </div>

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </div>
        </div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-20"
              data-testid="slider-prev"
            >
              <ChevronLeft size={24} className="text-[#0A0A0A]" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-20"
              data-testid="slider-next"
            >
              <ChevronRight size={24} className="text-[#0A0A0A]" />
            </button>
          </>
        )}

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => goToIndex(index)}
                className={`
                  flex-shrink-0 w-20 h-14 rounded-sm overflow-hidden
                  transition-all duration-300
                  ${index === currentIndex 
                    ? 'ring-2 ring-[#D4B982] ring-offset-2 opacity-100' 
                    : 'opacity-50 hover:opacity-80'
                  }
                `}
                data-testid={`thumbnail-${index}`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}

        {/* Dots Indicator */}
        {images.length > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToIndex(index)}
                className={`
                  w-2 h-2 rounded-full transition-all duration-300
                  ${index === currentIndex 
                    ? 'bg-[#D4B982] w-6' 
                    : 'bg-[#E4E4E7] hover:bg-[#D4B982]/50'
                  }
                `}
                data-testid={`dot-${index}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
            data-testid="lightbox-close"
          >
            <X size={24} className="text-white" />
          </button>

          {/* Main Image */}
          <div 
            className="relative max-w-[90vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`${title} - Bild ${index + 1}`}
                className={`
                  max-w-full max-h-[85vh] object-contain
                  transition-all duration-500 ease-out
                  ${index === currentIndex 
                    ? 'opacity-100 scale-100' 
                    : 'opacity-0 scale-95 absolute inset-0'
                  }
                `}
              />
            ))}
          </div>

          {/* Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={32} className="text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
                data-testid="lightbox-next"
              >
                <ChevronRight size={32} className="text-white" />
              </button>
            </>
          )}

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); goToIndex(index); }}
                  className={`
                    w-16 h-10 rounded overflow-hidden transition-all duration-300
                    ${index === currentIndex 
                      ? 'ring-2 ring-[#D4B982] opacity-100 scale-110' 
                      : 'opacity-40 hover:opacity-70'
                    }
                  `}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ImageSlider;
